import React from 'react';

import {
  PubSubMessageType,
  subscribe,
  SubscriptionTopic,
  unsubscribe,
} from '../../viewer/messaging/pubsub';

/**
 * Subscribes to specified topic and invokes the callback for each published
 * message WITHOUT storing it in React state — so the subscribing component is
 * not re-rendered on every message.
 *
 * Prefer this over {@link useSubscribe} whenever you only need the side-effect
 * (the common case). It matters for high-frequency topics such as
 * `WebsocketMessageRecieved`: with {@link useSubscribe}, every mounted
 * subscriber re-renders on every published message — even messages it ignores —
 * which can flood the main thread (e.g. during bulk image uploads) and starve
 * unrelated work like in-app navigation.
 */
export const useSubscribeCallback = <T extends SubscriptionTopic>(
  topic: T,
  callback: (message: PubSubMessageType<T>) => void | Promise<void>,
): void => {
  const callbackRef = React.useRef(callback);
  callbackRef.current = callback;

  React.useEffect(() => {
    const id = subscribe(topic, (message) => {
      void callbackRef.current?.(message);
    });
    return () => {
      unsubscribe(id);
    };
  }, [topic]);
};

/**
 * Subscribes to specified topic and returns message published for that topic.
 * Initially undefined is returned before the first message has been received.
 *
 * Note: this re-renders the subscribing component on every published message.
 * If you only need the callback side-effect, use {@link useSubscribeCallback}.
 */
export const useSubscribe = <T extends SubscriptionTopic>(
  topic: T,
  callback?: (message: PubSubMessageType<T>) => void | Promise<void>,
): PubSubMessageType<T> | undefined => {
  const [message, setMessage] = React.useState<PubSubMessageType<T>>();
  const callbackRef = React.useRef(callback);
  callbackRef.current = callback;

  React.useEffect(() => {
    const id = subscribe(topic, (message) => {
      setMessage(message);
      void callbackRef.current?.(message);
    });
    return () => {
      unsubscribe(id);
    };
  }, [topic]);

  return message;
};
